import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import client from "../api/client";
import { useDomainStore } from "../stores/domainStore";
import { useChatStore } from "../stores/chatStore";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Avatar, AvatarFallback } from "../components/ui/Avatar";
import StreamingMessage from "../components/StreamingMessage";
import TypingIndicator from "../components/ui/TypingIndicator";

export default function DomainChatPage() {
  const { id } = useParams();
  const { domains } = useDomainStore();
  const { conversations, sources, setMessages, appendMessage, updateLastMessage, setSources } = useChatStore();
  const domain = domains.find((d) => d.id === id);

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  const messages = conversations[id] || [
    { role: "assistant", content: `Ask questions about documents in ${domain?.name || "this domain"}. I'll search through the uploaded documents and provide cited answers.` },
  ];
  const currentSources = sources[id] || [];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = { role: "user", content: input };
    const updatedMessages = [...messages, userMsg];
    setMessages(id, updatedMessages);
    setInput("");
    setLoading(true);

    const history = updatedMessages
      .filter((m) => m.role !== "system")
      .slice(-10)
      .map((m) => ({ role: m.role, content: m.content }));

    try {
      const token = localStorage.getItem("accessToken");
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || "https://api.digitize.mr.jandayan.net"}/api/search/rag`, {
        method: "POST",
        headers,
        body: JSON.stringify({ query: userMsg.content, topK: 5, history, domain_id: id }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      // Extract sources from header before streaming
      const sourcesHeader = res.headers.get("X-Sources");
      let ragSources = [];
      if (sourcesHeader) {
        try {
          ragSources = JSON.parse(decodeURIComponent(sourcesHeader));
        } catch (e) {
          console.error("Failed to parse sources header:", e);
        }
      }
      setSources(id, ragSources);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = "";

      appendMessage(id, { role: "assistant", content: "" });

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        assistantContent += decoder.decode(value, { stream: true });
        updateLastMessage(id, assistantContent);
      }
    } catch (err) {
      appendMessage(id, { role: "assistant", content: `Error: ${err.message}`, isError: true });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex flex-col h-[calc(100vh-140px)]">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{domain?.name || "Domain"} Chat</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">AI-powered document Q&A</p>
        </div>
        <Badge variant="success">{domain?.role || "member"}</Badge>
      </div>

      <div className="flex-1 overflow-y-auto space-y-4 pr-2">
        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarFallback className={msg.role === "user" ? "bg-emerald-600 text-white" : "bg-slate-600 text-white"}>
                {msg.role === "user" ? "U" : "AI"}
              </AvatarFallback>
            </Avatar>
            <Card
              className={`max-w-[85%] p-4 ${
                msg.role === "user"
                  ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800"
                  : msg.isError
                  ? "bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800"
                  : "bg-slate-50 border-slate-200 dark:bg-slate-900 dark:border-slate-800"
              }`}
            >
              <p className={`text-xs font-semibold mb-2 ${msg.isError ? "text-red-600" : "text-slate-500"}`}>
                {msg.role === "user" ? "You" : "Assistant"}
              </p>
              {msg.role === "assistant" && !msg.isError ? (
                <StreamingMessage
                  content={msg.content}
                  sources={currentSources}
                  domainId={id}
                  isTyping={loading && i === messages.length - 1}
                />
              ) : (
                <div className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                  {msg.content}
                </div>
              )}
            </Card>
          </div>
        ))}
        {loading && messages[messages.length - 1]?.role !== "assistant" && (
          <div className="flex gap-3">
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarFallback className="bg-slate-600 text-white">AI</AvatarFallback>
            </Avatar>
            <Card className="max-w-[85%] p-4 bg-slate-50 border-slate-200 dark:bg-slate-900 dark:border-slate-800">
              <p className="text-xs font-semibold mb-1 text-slate-500">Assistant</p>
              <TypingIndicator />
            </Card>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={handleSubmit} className="mt-4 flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={`Ask about ${domain?.name || "documents"}...`}
          className="flex-1"
        />
        <Button type="submit" disabled={loading || !input.trim()}>
          Send
        </Button>
      </form>
    </div>
  );
}
